/**
 * El selector de período: día, semana, mes, año, todo o un rango a mano.
 *
 * Vive en la cabecera y manda sobre todas las pantallas a la vez, para que
 * Movimientos, Informes y el panel hablen siempre del mismo tramo de tiempo.
 *
 * En el móvil se enseña solo la flecha y el nombre del período, porque el ancho es
 * oro; al tocarlo se despliegan las opciones.
 */

import { CalendarDays, ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import { usePeriod, type PeriodKind } from '../lib/period';

const KINDS: ReadonlyArray<{ id: PeriodKind; label: string }> = [
  { id: 'day', label: 'Día' },
  { id: 'week', label: 'Semana' },
  { id: 'month', label: 'Mes' },
  { id: 'year', label: 'Año' },
  { id: 'all', label: 'Todo' },
  { id: 'custom', label: 'Elegir fechas' },
];

export function PeriodPicker(): React.JSX.Element {
  const period = usePeriod((s) => s.period);
  const range = usePeriod((s) => s.range);
  const setKind = usePeriod((s) => s.setKind);
  const setCustom = usePeriod((s) => s.setCustom);
  const shift = usePeriod((s) => s.shift);

  const [open, setOpen] = useState(false);
  const [from, setFrom] = useState(range.from);
  const [to, setTo] = useState(range.to);
  const container = useRef<HTMLDivElement>(null);

  // Al cambiar de período, los campos del rango a mano se ponen al día.
  useEffect(() => {
    setFrom(range.from);
    setTo(range.to);
  }, [range.from, range.to]);

  // Se cierra al tocar fuera o con Escape, como cualquier menú.
  useEffect(() => {
    if (!open) return;

    const onClick = (event: MouseEvent): void => {
      if (container.current && !container.current.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') setOpen(false);
    };

    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // «Todo el historial» no tiene anterior ni siguiente que tengan sentido.
  const canShift = period.kind !== 'all' && period.kind !== 'custom';

  return (
    <div className="relative" ref={container}>
      <div className="flex items-center gap-0.5 rounded-ed border border-ed-bg-border/40 bg-ed-bg-card/60">
        <button
          type="button"
          className="ed-btn-ghost !min-h-[36px] !px-1.5"
          aria-label="Período anterior"
          disabled={!canShift}
          onClick={() => shift(-1)}
        >
          <ChevronLeft size={16} aria-hidden="true" />
        </button>

        <button
          type="button"
          className="flex min-h-[36px] items-center gap-1.5 px-1 text-xs font-medium capitalize"
          aria-expanded={open}
          aria-haspopup="menu"
          onClick={() => setOpen((v) => !v)}
        >
          <CalendarDays size={14} className="text-ed-gold" aria-hidden="true" />
          <span className="max-w-[9rem] truncate">{range.label}</span>
          <ChevronDown size={13} aria-hidden="true" />
        </button>

        <button
          type="button"
          className="ed-btn-ghost !min-h-[36px] !px-1.5"
          aria-label="Período siguiente"
          disabled={!canShift}
          onClick={() => shift(1)}
        >
          <ChevronRight size={16} aria-hidden="true" />
        </button>
      </div>

      {open && (
        <div
          className="absolute right-0 z-50 mt-1 w-64 rounded-ed border border-ed-bg-border/50 bg-ed-bg-card p-2 shadow-ed-card"
          role="menu"
        >
          <p className="px-2 pb-1 pt-1 text-[11px] uppercase tracking-wider text-ed-text-muted">
            Ver por
          </p>

          <ul className="space-y-0.5">
            {KINDS.map((kind) => (
              <li key={kind.id}>
                <button
                  type="button"
                  role="menuitem"
                  className={`w-full rounded-ed px-2 py-2 text-left text-sm ${
                    period.kind === kind.id
                      ? 'bg-ed-gold/15 font-medium text-ed-gold'
                      : 'hover:bg-ed-bg-elevated/60'
                  }`}
                  onClick={() => {
                    setKind(kind.id);
                    // «Elegir fechas» necesita el formulario, así que no se cierra.
                    if (kind.id !== 'custom') setOpen(false);
                  }}
                >
                  {kind.label}
                </button>
              </li>
            ))}
          </ul>

          {period.kind === 'custom' && (
            <div className="mt-2 space-y-2 border-t border-ed-bg-border/30 px-2 pt-2">
              <label className="block text-xs text-ed-text-muted">
                Desde
                <input
                  type="date"
                  className="ed-input mt-1 !min-h-[38px] text-sm"
                  value={from}
                  max={to}
                  onChange={(e) => setFrom(e.target.value)}
                />
              </label>
              <label className="block text-xs text-ed-text-muted">
                Hasta
                <input
                  type="date"
                  className="ed-input mt-1 !min-h-[38px] text-sm"
                  value={to}
                  min={from}
                  onChange={(e) => setTo(e.target.value)}
                />
              </label>
              <button
                type="button"
                className="ed-btn-primary w-full !min-h-[38px] text-sm"
                onClick={() => {
                  setCustom(from, to);
                  setOpen(false);
                }}
              >
                Aplicar
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
